document.addEventListener("DOMContentLoaded", function () {
  const expandableCards = document.querySelectorAll("[data-expandable-card]");
  const reviewCards = document.querySelectorAll("[data-review-card]");

  // Handle expandable card functionality
  expandableCards.forEach((card) => {
    let isExpanded = false;

    function toggleExpanded() {
      isExpanded = !isExpanded;
      card.classList.toggle("pvl-review-card--expanded", isExpanded);
      card.setAttribute("aria-expanded", isExpanded.toString());

      // Update aria-label to indicate state
      const baseLabel = card.getAttribute("aria-label").split(" (")[0];
      const stateLabel = isExpanded
        ? " (expanded - press Enter to collapse)"
        : " (press Enter to expand)";
      card.setAttribute("aria-label", baseLabel + stateLabel);
    }

    function handleKeydown(e) {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          // Only handle expansion if there's no lightbox video
          if (!card.hasAttribute("data-lightbox-trigger")) {
            toggleExpanded();
          }
          break;
        case "Escape":
          if (isExpanded) {
            e.preventDefault();
            isExpanded = false;
            card.classList.remove("pvl-review-card--expanded");
            card.setAttribute("aria-expanded", "false");
            const baseLabel = card.getAttribute("aria-label").split(" (")[0];
            card.setAttribute(
              "aria-label",
              baseLabel + " (press Enter to expand)",
            );
          }
          break;
      }
    }

    card.addEventListener("keydown", handleKeydown);

    // Initialize ARIA attributes
    card.setAttribute("aria-expanded", "false");
    const currentLabel = card.getAttribute("aria-label");
    card.setAttribute("aria-label", currentLabel + " (press Enter to expand)");
  });

  // Handle delayed video loading
  reviewCards.forEach((card) => {
    const videoContainer = card.querySelector("[data-video-container]");
    if (!videoContainer) return;

    const hasVideo = card.dataset.hasVideo === "true";
    if (!hasVideo) return;

    let hoverTimeout;
    let isVideoLoaded = false;
    const HOVER_DELAY = 2000; // 2 seconds

    function loadVideo() {
      if (isVideoLoaded) return;

      const videoSourcesJson = videoContainer.dataset.videoSources;

      if (videoSourcesJson) {
        try {
          const videoSources = JSON.parse(videoSourcesJson);

          if (videoSources && videoSources.length > 0) {
            // Create video element
            const video = document.createElement("video");
            video.className = "pvl-review-card__video";
            video.autoplay = true;
            video.loop = true;
            video.muted = true;
            video.controls = false;
            video.playsInline = true;

            // Add video sources
            videoSources.forEach((source) => {
              const sourceElement = document.createElement("source");
              sourceElement.src = source.url;
              sourceElement.type = `video/${source.format}`;
              video.appendChild(sourceElement);
            });

            // Add video to container
            videoContainer.appendChild(video);

            // Mark as loaded
            isVideoLoaded = true;
            card.dataset.videoDelayLoaded = "true";

            // Show video overlay after brief delay for loading
            setTimeout(() => {
              videoContainer.classList.remove(
                "pvl-review-card__video-overlay--hidden",
              );
            }, 100);
          }
        } catch (error) {
          console.warn("Failed to parse video sources:", error);
        }
      }
    }

    function handleMouseEnter() {
      if (isVideoLoaded) {
        // If already loaded, show immediately
        videoContainer.classList.remove(
          "pvl-review-card__video-overlay--hidden",
        );
        return;
      }

      // Start countdown to load video
      hoverTimeout = setTimeout(() => {
        loadVideo();
      }, HOVER_DELAY);
    }

    function handleMouseLeave() {
      // Clear the loading timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }

      // Hide video if it was loaded
      if (isVideoLoaded) {
        videoContainer.classList.add("pvl-review-card__video-overlay--hidden");
      }
    }

    // Add event listeners
    card.addEventListener("mouseenter", handleMouseEnter);
    card.addEventListener("mouseleave", handleMouseLeave);
  });
});

// Carousel Reviews Web Component
export class CarouselReviews extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.slideCount = 0;
    this.isScrolling = false;
    this.isUserNavigating = false; // Flag to prevent observer conflicts
    this.scrollTimeout = null;

    // DOM elements
    this.track = null;
    this.slides = [];
    this.prevBtn = null;
    this.nextBtn = null;
    this.dots = [];
    this.announcer = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  init() {
    // Find DOM elements with fallback selectors
    this.track = this.querySelector("[data-carousel-track]") || this.querySelector(".pvl-carousel-reviews__track");
    if (!this.track) {
      console.warn("Carousel track not found");
      return;
    }

    const carouselId = this.track.id || 'carousel';
    this.prevBtn = this.querySelector(`[data-carousel-prev="${carouselId}"]`) || this.querySelector(".pvl-carousel-reviews__nav--prev");
    this.nextBtn = this.querySelector(`[data-carousel-next="${carouselId}"]`) || this.querySelector(".pvl-carousel-reviews__nav--next");
    this.dots = [...this.querySelectorAll("[data-carousel-dot]") || this.querySelectorAll(".pvl-carousel-reviews__dot")];
    this.slides = [...this.track.querySelectorAll("li") || this.track.children];
    this.slideCount = parseInt(this.track.dataset.slideCount) || this.slides.length;
    
    console.log('Carousel initialized:', {
      track: !!this.track,
      prevBtn: !!this.prevBtn, 
      nextBtn: !!this.nextBtn,
      slideCount: this.slideCount,
      dotsCount: this.dots.length
    });

    // Create live announcement element
    this.createAnnouncer();

    // Set up IntersectionObserver for center detection
    this.setupIntersectionObserver();

    // Set up event listeners
    this.setupEventListeners();

    // Initialize accessibility
    this.initializeAccessibility();

    // Initial state - check if scrollable and update everything
    // Use requestAnimationFrame to ensure DOM is fully rendered before calculating positions
    requestAnimationFrame(() => {
      this.updateScrollableState();

      // Start at position 0 (first slide) for full left-to-right scrolling
      if (this.slideCount > 0) {
        console.log('Setting initial slide to 0 for full range scrolling');
        this.currentSlide = 0;
        this.track.scrollLeft = 0; // Ensure we start at the beginning
        this.updateCenterSlide(0);
      }

      this.updateNavigation();
      this.updateDots();
    });
  }

  createAnnouncer() {
    this.announcer = document.createElement("div");
    this.announcer.setAttribute("aria-live", "polite");
    this.announcer.setAttribute("aria-atomic", "true");
    this.announcer.className = "sr-only";
    this.announcer.style.cssText =
      "position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;";
    this.appendChild(this.announcer);
  }

  setupIntersectionObserver() {
    // Use the carousel container as root instead of the scrolling track
    const carouselContainer = this.track.parentElement;

    const options = {
      root: carouselContainer,
      threshold: [0.5], // Simplified threshold for more reliable detection
      rootMargin: "0px -40% 0px -40%", // Center 20% detection zone
    };

    this.intersectionObserver = new IntersectionObserver(
      this.handleIntersection.bind(this),
      options,
    );

    // Observe all slides
    this.slides.forEach((slide) => {
      this.intersectionObserver.observe(slide);
    });

    // Also set up a fallback scroll-based detection
    this.setupScrollBasedDetection();
  }

  setupScrollBasedDetection() {
    // Fallback method using scroll position calculation
    this.track.addEventListener(
      "scroll",
      () => {
        if (this.isUserNavigating) {
          console.log('User navigating, skipping scroll detection');
          return;
        }

        clearTimeout(this.scrollDetectionTimeout);
        this.scrollDetectionTimeout = setTimeout(() => {
          this.detectCenterSlideByPosition();
        }, 150); // Increased debounce for stability
      },
      { passive: true },
    );
  }

  detectCenterSlideByPosition() {
    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestSlide = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(trackCenter - slideCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = index;
      }
    });

    // Only update if the center slide actually changed
    if (closestSlide !== this.currentSlide) {
      this.updateCenterSlide(closestSlide);
    }
  }

  handleIntersection(entries) {
    // Don't update state if user is actively navigating with dots/buttons
    if (this.isUserNavigating) {
      console.log('User navigating, skipping intersection update');
      return;
    }

    // Find the entry with highest intersection ratio for more accurate center detection
    let bestEntry = null;
    let highestRatio = 0;

    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
        highestRatio = entry.intersectionRatio;
        bestEntry = entry;
      }
    });

    if (bestEntry) {
      const slideIndex = this.slides.indexOf(bestEntry.target);
      if (slideIndex !== -1 && slideIndex !== this.currentSlide) {
        console.log('Intersection detected new center slide:', slideIndex);
        this.updateCenterSlide(slideIndex);
      }
    }
  }

  // Centralized method for updating center slide
  updateCenterSlide(slideIndex) {
    if (slideIndex < 0 || slideIndex >= this.slideCount) return;

    const previousSlide = this.currentSlide;
    this.currentSlide = slideIndex;

    // Update all card states first
    this.slides.forEach((slide, index) => {
      const reviewCard = slide.querySelector("[data-review-card]");
      if (reviewCard) {
        if (index === slideIndex) {
          // Center this card
          reviewCard.classList.add("pvl-review-card--centered");
          reviewCard.setAttribute("data-centered", "true");

          // Force content reveal by also adding a backup class
          reviewCard.classList.add("pvl-review-card--force-reveal");

          // Ensure proper ARIA states for selected card
          reviewCard.setAttribute("aria-hidden", "false");
          if (reviewCard.hasAttribute("tabindex")) {
            reviewCard.setAttribute("tabindex", "0");
          }
        } else {
          // Un-center other cards
          reviewCard.classList.remove("pvl-review-card--centered");
          reviewCard.classList.remove("pvl-review-card--force-reveal");
          reviewCard.setAttribute("data-centered", "false");

          // Update ARIA states for non-selected cards
          const isScrollable = this.isScrollable();
          if (!isScrollable) {
            // In non-scrollable mode, hide non-selected content
            reviewCard.setAttribute("aria-hidden", "true");
            if (reviewCard.hasAttribute("tabindex")) {
              reviewCard.setAttribute("tabindex", "-1");
            }
          } else {
            // In scrollable mode, keep all content accessible
            reviewCard.setAttribute("aria-hidden", "false");
          }
        }
      }
    });

    // Then update dots and navigation
    this.updateDots();
    this.updateNavigation();

    // Announce change
    if (this.announcer && previousSlide !== slideIndex) {
      this.announcer.textContent = `Showing review ${slideIndex + 1} of ${this.slideCount}`;
    }
  }

  setupEventListeners() {
    // Navigation buttons with enhanced scroll logic
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", (e) => {
        e.preventDefault();
        console.log('Prev button clicked');
        // Use percentage scrolling for better coverage, or slide for precision
        const scrollType = this.slideCount > 5 ? 'percentage' : 'slide';
        this.scrollBy("prev", scrollType);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", (e) => {
        e.preventDefault();
        console.log('Next button clicked');
        // Use percentage scrolling for better coverage, or slide for precision  
        const scrollType = this.slideCount > 5 ? 'percentage' : 'slide';
        this.scrollBy("next", scrollType);
      });
    }

    // Dot navigation with proper state management
    this.dots.forEach((dot, index) => {
      // Add data attributes for better tracking
      dot.setAttribute("data-slide-index", index);
      dot.setAttribute("data-carousel-id", this.track.id);

      dot.addEventListener("click", (e) => this.handleDotClick(e, index));
      dot.addEventListener("keydown", (e) => this.handleDotKeydown(e, index));
    });

    // Card focus listeners for bidirectional sync
    this.slides.forEach((slide, index) => {
      const reviewCard = slide.querySelector("[data-review-card]");
      if (reviewCard) {
        reviewCard.addEventListener("focus", () => this.handleCardFocus(index));
      }
    });

    // Scroll position tracking for button states
    this.track.addEventListener(
      "scroll",
      this.handleScrollPosition.bind(this),
      { passive: true },
    );

    // Enhanced resize observer for responsive behavior
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      this.resizeObserver.observe(this.track);
    }

    // Orientation change handling for mobile
    window.addEventListener("orientationchange", () => {
      setTimeout(() => this.handleResize(), 200);
    });

    // Add mobile detection
    this.isMobile = this.isMobileDevice();

    // Keyboard navigation for carousel
    this.addEventListener("keydown", this.handleCarouselKeydown.bind(this));
  }

  // Enhanced scrolling method with both slide-based and percentage-based options
  scrollBy(direction, scrollType = 'slide') {
    console.log('ScrollBy called:', direction, 'current slide:', this.currentSlide, 'type:', scrollType);
    
    // Prevent multiple rapid clicks
    if (this.isScrolling) {
      console.log('Already scrolling, ignoring');
      return;
    }

    this.isScrolling = true;
    this.isUserNavigating = true;

    if (scrollType === 'percentage') {
      // Percentage-based scrolling for full range coverage
      this.scrollByPercentage(direction);
    } else {
      // Slide-based scrolling
      let targetSlide;
      if (direction === "prev") {
        targetSlide = Math.max(0, this.currentSlide - 1);
      } else {
        targetSlide = Math.min(this.slideCount - 1, this.currentSlide + 1);
      }

      console.log('Target slide:', targetSlide);
      this.updateCenterSlide(targetSlide);
      this.goToSlide(targetSlide);
    }

    // Re-enable intersection observer and reset scroll flag after animation
    setTimeout(() => {
      this.updateNavigation();
      this.isScrolling = false;
      this.isUserNavigating = false;
      console.log('Scroll complete, flags reset');
    }, 400);
  }

  // Percentage-based scrolling for full left-to-right coverage
  scrollByPercentage(direction) {
    const containerWidth = this.track.clientWidth;
    const scrollWidth = this.track.scrollWidth;
    const currentScroll = this.track.scrollLeft;
    const maxScroll = scrollWidth - containerWidth;
    
    // Scroll by container width for full coverage
    const scrollAmount = containerWidth * 0.8; // 80% of container width
    
    let targetScroll;
    if (direction === "prev") {
      targetScroll = Math.max(0, currentScroll - scrollAmount);
    } else {
      targetScroll = Math.min(maxScroll, currentScroll + scrollAmount);
    }

    console.log('Percentage scroll:', {
      direction,
      containerWidth,
      scrollWidth,
      currentScroll,
      maxScroll,
      scrollAmount,
      targetScroll
    });

    this.track.scrollTo({
      left: targetScroll,
      behavior: "smooth"
    });

    // Update current slide based on new scroll position
    setTimeout(() => {
      this.detectCenterSlideByPosition();
    }, 100);
  }

  // Handle dot click with proper state management
  handleDotClick(event, slideIndex) {
    event.preventDefault();

    // Temporarily disable intersection observer to prevent conflicts
    this.isUserNavigating = true;

    // Use centralized method to update center slide
    this.updateCenterSlide(slideIndex);

    // For scrollable carousels, scroll to the target slide
    const isScrollable = this.isScrollable();
    if (isScrollable) {
      this.goToSlide(slideIndex);
    }

    // Re-enable intersection observer after operation completes
    setTimeout(
      () => {
        this.isUserNavigating = false;
        console.log('User navigation flag cleared after dot click');
      },
      isScrollable ? 800 : 100,
    );
  }

  // Discrete slide navigation - scroll by full slide widths from left to right
  goToSlide(slideIndex) {
    console.log('GoToSlide called:', slideIndex, 'total slides:', this.slideCount);
    
    if (slideIndex < 0 || slideIndex >= this.slideCount) {
      console.log('Invalid slide index');
      return;
    }

    const slide = this.slides[slideIndex];
    if (!slide) {
      console.log('Slide element not found at index:', slideIndex);
      return;
    }

    const containerWidth = this.track.clientWidth;
    const slideWidth = slide.offsetWidth;
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;

    // Calculate scroll position: slide position without centering offset
    const slideOffsetLeft = slide.offsetLeft;
    
    // For left-to-right scrolling, use the slide's actual position
    // Adjust only to ensure the slide is fully visible
    const targetPosition = Math.max(0, slideOffsetLeft - 30); // Small offset for padding
    
    // Ensure we don't scroll past the maximum
    const maxScroll = this.track.scrollWidth - containerWidth;
    const finalPosition = Math.min(targetPosition, maxScroll);

    console.log('Scroll calculations (left-to-right):', {
      slideOffsetLeft,
      containerWidth,
      slideWidth,
      gap,
      targetPosition,
      finalPosition,
      maxScroll
    });

    // Use smooth scrolling
    this.track.scrollTo({
      left: finalPosition,
      behavior: "smooth",
    });

    if (this.announcer) {
      this.announcer.textContent = `Showing review ${slideIndex + 1} of ${this.slideCount}`;
    }
  }

  // Check if content is scrollable
  isScrollable() {
    const scrollWidth = this.track.scrollWidth;
    const clientWidth = this.track.clientWidth;
    return scrollWidth > clientWidth + 10; // 10px threshold for floating point precision
  }

  // Update scrollable state and button visibility
  updateScrollableState() {
    const isScrollable = this.isScrollable();
    console.log('Updating scrollable state:', isScrollable);

    if (this.prevBtn) {
      if (isScrollable) {
        this.prevBtn.style.opacity = "";
        this.prevBtn.style.pointerEvents = "";
        this.prevBtn.style.visibility = "visible";
      } else {
        this.prevBtn.style.opacity = "0";
        this.prevBtn.style.pointerEvents = "none";
        this.prevBtn.style.visibility = "hidden";
      }
    }

    if (this.nextBtn) {
      if (isScrollable) {
        this.nextBtn.style.opacity = "";
        this.nextBtn.style.pointerEvents = "";
        this.nextBtn.style.visibility = "visible";
      } else {
        this.nextBtn.style.opacity = "0";
        this.nextBtn.style.pointerEvents = "none";
        this.nextBtn.style.visibility = "hidden";
      }
    }

    // For non-scrollable carousels, start at first slide (not centered)
    if (!isScrollable && this.dots.length > 0) {
      if (this.currentSlide !== 0) {
        this.updateCenterSlide(0);
      }
    }

    // Update all dots to reflect the new scrollable state
    this.updateDots();

    return isScrollable;
  }

  // Handle card focus for bidirectional sync
  handleCardFocus(cardIndex) {
    // Update dot state when card is focused
    this.currentSlide = cardIndex;
    this.updateDots();

    // Ensure the focused card content is revealed
    const slide = this.slides[cardIndex];
    const reviewCard = slide?.querySelector("[data-review-card]");
    if (reviewCard) {
      reviewCard.classList.add("pvl-review-card--centered");
    }
  }

  // Handle scroll position for button states
  handleScrollPosition() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      this.updateNavigation();
      // Also update current slide based on scroll position for better sync
      if (!this.isUserNavigating) {
        this.detectCenterSlideByPosition();
      }
    }, 50);
  }

  // Enhanced resize handling with scroll position preservation
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Store current slide before resize calculations
      const previousSlide = this.currentSlide;
      const wasScrollable = this.isScrollable();

      // Recalculate scrollable state (this will auto-set center slide if needed)
      this.updateScrollableState();
      const isNowScrollable = this.isScrollable();

      // If scrollability changed, handle accordingly
      if (wasScrollable && !isNowScrollable) {
        // Became non-scrollable: reset to first slide
        this.track.scrollLeft = 0;
        this.updateCenterSlide(0);
      } else if (!wasScrollable && isNowScrollable) {
        // Became scrollable: maintain current slide if valid, otherwise start from beginning
        if (previousSlide >= 0 && previousSlide < this.slideCount) {
          this.updateCenterSlide(previousSlide);
          setTimeout(() => {
            this.goToSlide(previousSlide);
          }, 50);
        } else {
          this.track.scrollLeft = 0;
          this.updateCenterSlide(0);
        }
      } else if (isNowScrollable) {
        // Still scrollable: maintain scroll position and detect current slide
        this.detectCenterSlideByPosition();
      }

      // Update all states
      this.updateNavigation();
      this.updateDots();
    }, 150); // Debounce resize events
  }

  // Mobile device detection
  isMobileDevice() {
    return (
      /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      ) || window.innerWidth < 768
    );
  }

  updateNavigation() {
    const isScrollable = this.isScrollable();
    const currentScroll = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    
    // Check actual scroll position for more accurate boundary detection
    const atStart = currentScroll <= 1; // Small threshold for floating point precision
    const atEnd = currentScroll >= maxScroll - 1;
    
    console.log('Updating navigation:', {
      isScrollable,
      currentSlide: this.currentSlide,
      slideCount: this.slideCount,
      currentScroll,
      maxScroll,
      atStart,
      atEnd
    });

    // Update previous button
    if (this.prevBtn) {
      this.prevBtn.disabled = !isScrollable || atStart;

      // Apply visual state
      if (!isScrollable) {
        this.prevBtn.style.opacity = "0";
        this.prevBtn.style.pointerEvents = "none";
        this.prevBtn.style.visibility = "hidden";
      } else if (atStart) {
        this.prevBtn.style.opacity = "0.3";
        this.prevBtn.style.pointerEvents = "none";
        this.prevBtn.style.visibility = "visible";
      } else {
        this.prevBtn.style.opacity = "1";
        this.prevBtn.style.pointerEvents = "auto";
        this.prevBtn.style.visibility = "visible";
      }

      this.prevBtn.setAttribute(
        "aria-label",
        atStart || !isScrollable
          ? "Previous review (unavailable)"
          : "Previous review",
      );
    }

    // Update next button
    if (this.nextBtn) {
      this.nextBtn.disabled = !isScrollable || atEnd;

      // Apply visual state
      if (!isScrollable) {
        this.nextBtn.style.opacity = "0";
        this.nextBtn.style.pointerEvents = "none";
        this.nextBtn.style.visibility = "hidden";
      } else if (atEnd) {
        this.nextBtn.style.opacity = "0.3";
        this.nextBtn.style.pointerEvents = "none";
        this.nextBtn.style.visibility = "visible";
      } else {
        this.nextBtn.style.opacity = "1";
        this.nextBtn.style.pointerEvents = "auto";
        this.nextBtn.style.visibility = "visible";
      }

      this.nextBtn.setAttribute(
        "aria-label",
        atEnd || !isScrollable ? "Next review (unavailable)" : "Next review",
      );
    }
  }

  updateDots() {
    if (!this.dots || this.dots.length === 0) return;

    const isScrollable = this.isScrollable();

    // Ensure we have a valid current slide
    if (this.currentSlide < 0 || this.currentSlide >= this.dots.length) {
      this.currentSlide = isScrollable ? 0 : Math.floor(this.dots.length / 2);
    }

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlide;

      // Clear all previous active states first
      dot.classList.remove("pvl-carousel-reviews__dot--active");

      // Set active state for current dot
      if (isActive) {
        dot.classList.add("pvl-carousel-reviews__dot--active");
      }

      // Update data attributes for state tracking
      dot.setAttribute("data-active", isActive.toString());
      dot.setAttribute("data-current-slide", this.currentSlide.toString());
      dot.setAttribute("data-scrollable", isScrollable.toString());

      // Update ARIA attributes
      dot.setAttribute("aria-selected", isActive.toString());
      dot.setAttribute("tabindex", isActive ? "0" : "-1");

      // Update dot aria-label based on mode
      const baseLabel = `Go to review ${index + 1}`;
      const modeLabel = isScrollable
        ? " (scroll to view)"
        : " (select to highlight)";
      dot.setAttribute("aria-label", baseLabel + modeLabel);

      // Ensure dots remain clickable even in non-scrollable mode
      dot.style.pointerEvents = "";
      dot.style.cursor = "pointer";
    });
  }

  updateSlideARIA() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentSlide;
      slide.setAttribute("role", "group");
      slide.setAttribute("aria-roledescription", "slide");
      slide.setAttribute(
        "aria-label",
        `Review ${index + 1} of ${this.slideCount}`,
      );
      slide.setAttribute("aria-hidden", (!isActive).toString());
    });
  }

  handleCarouselKeydown(e) {
    if (e.target !== this && e.target !== this.track) return;

    switch (e.key) {
      case "ArrowLeft":
        e.preventDefault();
        this.scrollBy("prev");
        break;
      case "ArrowRight":
        e.preventDefault();
        this.scrollBy("next");
        break;
      case "Home":
        e.preventDefault();
        this.goToSlide(0);
        break;
      case "End":
        e.preventDefault();
        this.goToSlide(this.slideCount - 1);
        break;
    }
  }

  handleDotKeydown(e, index) {
    let newIndex = index;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        newIndex = index > 0 ? index - 1 : this.dots.length - 1;
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        newIndex = index < this.dots.length - 1 ? index + 1 : 0;
        break;
      case "Home":
        e.preventDefault();
        newIndex = 0;
        break;
      case "End":
        e.preventDefault();
        newIndex = this.dots.length - 1;
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        this.handleDotClick(e, index);
        return;
    }

    if (newIndex !== index) {
      this.dots[newIndex].focus();
    }
  }

  initializeAccessibility() {
    // Set up proper focus management
    this.setAttribute("tabindex", "0");

    // Add instructions for screen readers
    const instructions = document.createElement("div");
    instructions.className = "sr-only";
    instructions.id = `carousel-instructions-${this.track.id}`;
    instructions.textContent =
      "Use arrow keys to navigate slides, or tab to navigation controls";
    this.appendChild(instructions);
    this.setAttribute("aria-describedby", instructions.id);

    // Initialize slide ARIA states
    this.updateSlideARIA();

    // Set up review cards for proper accessibility
    this.slides.forEach((slide, index) => {
      const reviewCard = slide.querySelector("[data-review-card]");
      if (reviewCard) {
        const existingLabel = reviewCard.getAttribute("aria-label") || "";
        const slideLabel = `Review ${index + 1} of ${this.slideCount}`;
        if (!existingLabel.includes(slideLabel)) {
          reviewCard.setAttribute(
            "aria-label",
            `${existingLabel} - ${slideLabel}`.trim(),
          );
        }
      }
    });
  }

  cleanup() {
    // Disconnect observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Remove event listeners
    if (this.track) {
      this.track.removeEventListener(
        "scroll",
        this.handleScrollPosition.bind(this),
      );
    }

    this.removeEventListener("keydown", this.handleCarouselKeydown.bind(this));

    // Remove orientation change listener
    window.removeEventListener(
      "orientationchange",
      this.handleResize.bind(this),
    );

    // Clear all timeouts
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    if (this.scrollDetectionTimeout) {
      clearTimeout(this.scrollDetectionTimeout);
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}

// Register the custom element
customElements.define("carousel-reviews", CarouselReviews);

// Fallback initialization for existing carousels on page
document.addEventListener("DOMContentLoaded", function() {
  // Find any carousel elements that might not have been initialized
  const carousels = document.querySelectorAll('carousel-reviews');
  console.log('Found carousels on page:', carousels.length);
  
  carousels.forEach((carousel, index) => {
    console.log(`Checking carousel ${index + 1}:`, {
      hasTrack: !!carousel.querySelector('[data-carousel-track]'),
      hasButtons: !!(carousel.querySelector('.pvl-carousel-reviews__prev') && carousel.querySelector('.pvl-carousel-reviews__next')),
      hasDots: !!carousel.querySelector('.pvl-carousel-reviews__dot')
    });
  });
});
